import { ReportSection } from '@mai-team-amusement/core';
import { useCommonT, useLocaleState } from '@mai-team-amusement/ui';
import { RevisionKind, type ReportView, type RevisionView } from '@mai-work-report/contracts';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { ReportQueryKey, reportApi } from '../api.js';
import { formatDateTime, formatDayWithWeekday } from '../format.js';
import { useReportT } from '../i18n/index.js';
import { QueryView, Space } from './common.js';
import { RichTextView } from './RichTextView.js';

const DRAWER_WIDTH_PX = 760;
const DRAWER_WIDTH = { xs: '100vw', md: DRAWER_WIDTH_PX };

function Revision({ revision, timeZone }: { revision: RevisionView; timeZone: string }) {
  const t = useReportT();
  const common = useCommonT();
  const { locale } = useLocaleState();
  const section =
    revision.section === ReportSection.PlanThisWeek
      ? t('revisions.planThisWeek')
      : revision.section === ReportSection.PlanNextWeek
        ? t('revisions.planNextWeek')
        : t('revisions.day', { date: formatDayWithWeekday(revision.date ?? '', common) });
  return (
    <Stack spacing={Space.Xs}>
      <Stack direction="row" spacing={Space.Xs} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 'bold' }}>{section}</Typography>
        {revision.kind === RevisionKind.Inherited ? (
          <Chip size="small" label={t('revisions.inherited')} />
        ) : null}
        <Typography variant="body2" color="text.secondary">
          {revision.editor?.name ?? ''} {formatDateTime(revision.createdAt, timeZone, locale)}
        </Typography>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={Space.Sm}>
        <Box sx={{ flex: 1, minWidth: 0, bgcolor: 'action.hover', p: Space.Xs, borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('revisions.before')}
          </Typography>
          {revision.before === null ? (
            <Typography>—</Typography>
          ) : (
            <RichTextView value={revision.before} />
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            border: 1,
            borderColor: 'divider',
            p: Space.Xs,
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {t('revisions.after')}
          </Typography>
          <RichTextView value={revision.after} />
        </Box>
      </Stack>
    </Stack>
  );
}

/** Every saved change of the report, newest first, before and after side by side (read-only). */
export function RevisionDrawer({ view, onClose }: { view: ReportView; onClose: () => void }) {
  const t = useReportT();
  const revisions = useQuery({
    queryKey: [...ReportQueryKey.Revisions, view.user.sub, view.weekStart],
    queryFn: () => reportApi.revisions(view.user.sub, view.weekStart),
  });
  return (
    <Drawer anchor="right" open onClose={onClose}>
      <Box
        sx={{ width: DRAWER_WIDTH, p: Space.Md }}
        role="region"
        aria-label={t('revisions.title')}
      >
        <Stack direction="row" sx={{ alignItems: 'center', mb: Space.Sm }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('revisions.title')}
          </Typography>
          <IconButton aria-label={t('revisions.title')} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <QueryView query={revisions}>
          {(data) =>
            data.revisions.length === 0 ? (
              <Typography color="text.secondary">{t('revisions.empty')}</Typography>
            ) : (
              <Stack spacing={Space.Sm} divider={<Divider flexItem />}>
                {data.revisions.map((revision) => (
                  <Revision key={revision.id} revision={revision} timeZone={view.timeZone} />
                ))}
              </Stack>
            )
          }
        </QueryView>
      </Box>
    </Drawer>
  );
}
